import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Flex, Layout, MenuProps, Row, message } from 'antd';
import {
  BottomSheet,
  TableAction,
  DownloadIcon,
  PickupsIcon,
  Heading,
  ExportIcon,
  ResolveIcon,
  DeleteIcon,
  PageLayout,
  OrdersPausedBanner,
} from '@/components';
import { RotateCw, Undo2 } from 'lucide-react';
import OrdersToolbar from './toolbar/OrdersToolbar';
import { useOrderTableColumns } from '@/hooks/useOrderTableColumns';
import { useTranslation } from 'react-i18next';
import { getColumns, getActionColumn, InventoryCallbacks } from './list.tsx';
import { OrderStatusTab, StatusEnum } from '@/types/columns.ts';
import { getBulkMenuItems } from './bulkMenuItems';
import styles from './orders.module.css';
import useOrderData, { OrderDataType } from '@/hooks/useOrderData.ts';
import useInventoryAsOrderData from '@/hooks/useInventoryAsOrderData.ts';
import useSubmitOrder from '@/hooks/useSubmitOrders.tsx';
import useTabVisibility from '@/hooks/useTabVisibility.ts';
import useSearchStore, { ISearch } from '@/hooks/useSearchStore.ts';
import useOrderListData from '@/hooks/useOrderListData.tsx';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { OrderColumn } from '@/types/order.ts';
import { PermissionName } from '@/types';
import BulkResolveModal from '@/components/ResolveModal/BulkResolveModal.tsx';
import DelayRtoConfirmModal from '@/components/Order/DelayRtoConfirmModal.tsx';
import { BulkOrderActions } from '@/components/Order/BulkActions/BulkOrderActions';
import {
  useBulkCancelOrders,
  useBulkDeleteOrders,
  useFetchOrderCount,
} from '@/hooksApi/useOrder.ts';
import { OrderStatusEnum } from '@/config/constants.ts';
import { useQueryClient } from '@tanstack/react-query';
import useConfirmModalStore from '@/stores/useConfirmModalStore.ts';
import { listStatusButtons } from '@/utils';
import { getTabItems } from './getTabsItems.tsx';
import useFilterOrderStore, { useOrderFilters } from '@/stores/useFilterOrderStore.ts';
import { useOrdersUrlSync } from '@/hooks/useOrdersUrlSync.ts';
import usePermission from '@/hooks/usePermission';
import { useResendStore } from '@/stores/useResendStore.ts';
import { InventoryOrder } from '@/types/inventory.ts';
import { ROUTE } from '@/config';
import useUserStore, { selectUsesServices } from '@/stores/userStore.ts';
import {
  EMPTY_EXPORT_SELECTION,
  selectOpenExport,
  selectSetExportSelection,
  useExportModalStore,
} from '@/stores/useExportModalStore';

const pageSize = 25;

const Orders = () => {
  //* States
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isBulkResolveOpen, setIsBulkResolveOpen] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<OrderStatusTab>(OrderStatusTab.READY_TO_SHIP);
  const [activeSubTab, setActiveSubTab] = useState<string>('all');
  // Flag: auto-select all orders once data loads after mixed-status filter switch
  const [pendingAutoSelect, setPendingAutoSelect] = useState(false);

  // Merchant settings (feature flags)
  const { user } = useUserStore();
  const usesServices = useUserStore(selectUsesServices);
  // Show Inventory tab if the setting is explicitly true, OR if the setting object
  // is null/undefined (backend may not return it for all merchants — default to showing the tab)
  const isDelayRtoEnabled =
    user?.setting === null || user?.setting === undefined
      ? true
      : (user?.setting?.is_delay_rto_and_resend_enabled ?? true);

  // Inventory-tab-specific state
  const [inventoryPage, setInventoryPage] = useState(1);
  const [delayRtoModalOpen, setDelayRtoModalOpen] = useState(false);
  const [delayRtoTargetOrders, setDelayRtoTargetOrders] = useState<InventoryOrder[]>([]);

  //* Stores
  const {
    currentPage,
    createdAtFilter,
    searchValue: searchValueFilter,
    orderTypeFilter,
    printedFilter,
    setCurrentPage,
    resetFilers,
    setCreatedAt,
    setOrderType,
  } = useFilterOrderStore();
  const orderFilters = useOrderFilters();

  //* Zustand store values search
  const { searchValue: searchTermHeader, setSearchValue: setSearchTermHeader } = useSearchStore();

  // Inventory resend store
  const { setSelectedOrders: setResendOrders } = useResendStore();

  const { openConfirm } = useConfirmModalStore();

  //* React hooks

  const [searchParams, setSearchParams] = useSearchParams(); //!Params Related
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();

  //* APIs

  // Map frontend tab enum to backend status_group values (hyphenated)
  const STATUS_GROUP_MAP: Record<string, string> = {
    [OrderStatusTab.READY_TO_SHIP]: 'ready-to-ship',
    [OrderStatusTab.IN_PROGRESS]: 'in-progress',
    [OrderStatusTab.ON_HOLD]: 'on-hold',
    [OrderStatusTab.RTO]: 'rto',
    [OrderStatusTab.FINISHED]: 'finished',
  };

  // Map sub-tab values to backend status.slug for Meilisearch filtering
  // Only pass when a specific sub-tab is selected (not 'all')
  const subTabStatusSlug = activeSubTab !== 'all' ? activeSubTab : undefined;

  // Fetch OrderList by search and filter — scope to active tab via statusGroup
  const { listData, fetchingOrderList, totalsOrderList } = useOrderListData(
    currentPage,
    pageSize,
    searchTermHeader,
    orderFilters,
    STATUS_GROUP_MAP[activeTab], // backend expects hyphenated values
    subTabStatusSlug, // filter by specific sub-status within the tab
  );
  // Fetch OrderList By status and subStatus.
  // When INVENTORY tab is active, InventoryTab handles its own data fetching, so we pass a
  // safe non-INVENTORY tab (READY_TO_SHIP) to keep the hook chain inside useOrderData stable.
  // The results are simply unused while INVENTORY is displayed.
  const { activeData, totals, isFetching, refetch } = useOrderData(
    currentPage,
    activeTab === OrderStatusTab.INVENTORY ? OrderStatusTab.READY_TO_SHIP : activeTab,
    activeTab === OrderStatusTab.INVENTORY ? 'all' : activeSubTab,
    pageSize,
    orderFilters,
  );
  const { data: orderCount } = useFetchOrderCount(hasPermission(PermissionName.DASHBOARD));

  // Inventory data — always fetched (hook call must be unconditional)
  const {
    activeData: inventoryData,
    rawItems: inventoryRawItems,
    totals: inventoryTotals,
    isFetching: isInventoryFetching,
  } = useInventoryAsOrderData(inventoryPage, pageSize);

  // Mutations APIs
  const { mutate: bulkDeleteOrder, isLoading: isBulkDeleteLoading } = useBulkDeleteOrders();
  const { mutate: bulkCancelOrder, isLoading: isBulkCancelLoading } = useBulkCancelOrders();

  // Determine the effective bulk action for the "With You" tab based on status composition
  const readyToShipCounts = orderCount?.object?.[OrderStatusTab.READY_TO_SHIP];
  const hasNewOrders = (readyToShipCounts?.new ?? 0) > 0;
  const hasScheduledOrders = (readyToShipCounts?.scheduled ?? 0) > 0;
  const isMixedReadyToShip =
    activeTab === OrderStatusTab.READY_TO_SHIP &&
    activeSubTab === 'all' &&
    hasNewOrders &&
    hasScheduledOrders;
  const isScheduledOnly =
    activeTab === OrderStatusTab.READY_TO_SHIP &&
    (activeSubTab === 'scheduled' ||
      (activeSubTab === 'all' && hasScheduledOrders && !hasNewOrders));

  // handle request pickup / download labels
  const { onSubmit, LoadingBulkRequestPickup } = useSubmitOrder(
    activeTab,
    selectedOrderIds,
    isAllSelected,
    activeSubTab,
    isScheduledOnly,
  );

  //* Get Params
  // parse query parameters   //!Params Related
  const queryParams = new URLSearchParams(location.search);
  const activeTabQuery = queryParams.get('activeTab') as OrderStatusTab;
  const activeSubTabQuery = queryParams.get('activeSubTab') as OrderStatusTab;
  // Search is now handled by /search page — no longer read from URL here

  // * CustomHooks

  // tabs items
  const tabItems = getTabItems({
    t,
    i18n,
    checkActive,
    handleChangeSubTab,
    orderCount: orderCount?.object ? { ...orderCount?.object } : undefined,
    inventoryTotal: inventoryTotals > 0 ? inventoryTotals : undefined,
    isDelayRtoEnabled,
  });

  // Hook to handle tab visibility
  useTabVisibility(
    () => {},
    () => {
      // ? IMPROVEMENT: set queryKeys to invalidate specific APIs
      queryClient.invalidateQueries();
    },
  );

  useOrdersUrlSync({
    searchStore: { searchValue: searchTermHeader, setSearchValue: setSearchTermHeader },
    filterStore: {
      createdAtFilter,
      orderTypeFilter,
      currentPage,
      setCreatedAt,
      setOrderType,
      setCurrentPage,
    },
    resetFilers: resetFilers,
  });

  // Inventory-tab action callbacks — navigate to resend page or open return modal.
  // Use inventoryRawItems lookup to get the full InventoryOrder with all fields
  // (region_id, city_id, available_carriers_detail) instead of fabricated stubs.
  const navigate = useNavigate();
  const inventoryCallbacks: InventoryCallbacks = {
    onResend: (order: InventoryOrder) => {
      const realOrder = inventoryRawItems.find((o) => o.uid === order.uid) ?? order;
      setResendOrders([realOrder]);
      navigate(ROUTE.RESEND);
    },
    onReturnNow: (order: InventoryOrder) => {
      const realOrder = inventoryRawItems.find((o) => o.uid === order.uid) ?? order;
      setDelayRtoTargetOrders([realOrder]);
      setDelayRtoModalOpen(true);
    },
  };

  // Call getActionColumn unconditionally here so its internal hooks (useState,
  // useRequestPickup, usePermission, useQueryClient, useNavigate) are always
  // called in the same order on every render — regardless of which tab is active.
  // Previously this was called inside the getColumns() JSX prop expression which
  // caused it to be skipped when the INVENTORY tab rendered <InventoryTab />,
  // violating React's rules of hooks ("Rendered fewer hooks than expected").
  const actionColumn = getActionColumn(
    searchTermHeader?.search ? undefined : activeTab,
    activeTab === OrderStatusTab.INVENTORY ? inventoryCallbacks : undefined,
  );

  // Owner 11-02: the merchant chooses which columns show (Columns menu next to Filter).
  const tableColumns = useOrderTableColumns(
    getColumns(
      !!searchTermHeader?.search,
      activeTab,
      activeSubTab,
      t,
      actionColumn,
      usesServices,
    ) || [],
  );

  //* Constants

  // Tabs whose bulk actions (ship, labels, cancel) can act on "all N" server-side.
  // BOXY-409: select-all itself is offered on every tab; elsewhere it only exports.
  const bulkSupportsSelectAll =
    activeTab === OrderStatusTab.READY_TO_SHIP ||
    (activeTab === OrderStatusTab.IN_PROGRESS &&
      [OrderStatusEnum.scheduled, OrderStatusEnum.collecting, OrderStatusEnum.collected].includes(
        activeSubTab as OrderStatusEnum,
      ));

  const currentPageRows = activeTab === OrderStatusTab.INVENTORY ? inventoryData : activeData;
  const allOrdersInCurrentPage = useMemo(() => {
    const result = currentPageRows.map((item) => item.key);
    if (isAllSelected) setSelectedOrderIds(result);
    return result;
  }, [currentPageRows[0]?.key]);
  const exportOnlySelectAll = isAllSelected && !bulkSupportsSelectAll;

  // BOXY-409 — the export modal (header icon or bottom bar) reads this selection.
  const openExport = useExportModalStore(selectOpenExport);
  const setExportSelection = useExportModalStore(selectSetExportSelection);
  const selectedTotal = activeTab === OrderStatusTab.INVENTORY ? inventoryTotals : totals;
  useEffect(() => {
    setExportSelection({
      uids: selectedOrderIds,
      isAllSelected,
      count: isAllSelected ? selectedTotal : selectedOrderIds.length,
    });
  }, [selectedOrderIds, isAllSelected, selectedTotal]);
  useEffect(() => () => setExportSelection(EMPTY_EXPORT_SELECTION), []);

  // Auto-select all orders after mixed-status filter switch
  useEffect(() => {
    if (pendingAutoSelect && activeData.length > 0) {
      setSelectedOrderIds(activeData.map((item) => item.key));
      setPendingAutoSelect(false);
    }
  }, [pendingAutoSelect, activeData]);

  const hasFilter = createdAtFilter || (orderTypeFilter && orderTypeFilter.length > 0);
  const hasSearchHeader = !!searchTermHeader?.search && searchTermHeader?.searchBy;
  // Only global text search switches to listData (search API).
  // Type/Date filters are handled by the tab-specific APIs via filterParams.
  const isSearched = !!hasSearchHeader;
  const isGlobalSearch = isSearched;

  const menuItems: MenuProps['items'] = [
    {
      key: 'export',
      label: (
        <Heading
          title={t('export_selected_orders')}
          leadingIcon={<ExportIcon />}
          rotation={false}
        />
      ),
      onClick: openExport,
    },
    {
      key: 'cancel',
      label: (
        <Heading
          title={
            activeTab === OrderStatusTab.READY_TO_SHIP && !isScheduledOnly
              ? t('delete')
              : t('cancel')
          }
          leadingIcon={<DeleteIcon />}
          rotation={false}
          loading={isBulkDeleteLoading || isBulkCancelLoading}
        />
      ),
      onClick: handleBulkDelete,
    },
    // FE-8: Bulk Resolve CSV — only meaningful on the On Hold tab where
    // merchants have many orders waiting on resolve or RTO-request decisions.
    ...(activeTab === OrderStatusTab.ON_HOLD
      ? [
          {
            key: 'bulk-resolve',
            label: (
              <Heading
                title={String(t('bulk_resolve_csv', { defaultValue: 'Bulk resolve via CSV' }))}
                leadingIcon={<RotateCw size={16} />}
                rotation={false}
              />
            ),
            onClick: () => setIsBulkResolveOpen(true),
          },
        ]
      : []),
  ];

  const inventoryMenuItems: MenuProps['items'] = [
    {
      key: 'resend-selected',
      label: (
        <Heading
          title={t('resend_selected')}
          leadingIcon={<RotateCw size={16} />}
          rotation={false}
        />
      ),
      onClick: () => {
        const ordersToResend = inventoryRawItems.filter((item) =>
          selectedOrderIds.includes(item.uid),
        );
        setResendOrders(ordersToResend);
        navigate(ROUTE.RESEND);
      },
    },
    {
      key: 'return-selected',
      label: (
        <Heading title={t('return_selected')} leadingIcon={<Undo2 size={16} />} rotation={false} />
      ),
      onClick: () => {
        const ordersToReturn = inventoryRawItems.filter((item) =>
          selectedOrderIds.includes(item.uid),
        );
        setDelayRtoTargetOrders(ordersToReturn);
        setDelayRtoModalOpen(true);
      },
    },
    {
      key: 'export',
      label: (
        <Heading
          title={t('export_selected_orders')}
          leadingIcon={<ExportIcon />}
          rotation={false}
        />
      ),
      onClick: openExport,
    },
  ];
  // BOXY-376 — the selected rows the bulk Resend / Request RTO buttons count.
  const selectedRows = useMemo(
    () => activeData.filter((row) => selectedOrderIds.includes(row.key)),
    [activeData, selectedOrderIds],
  );

  const handleBulkActionDone = () => {
    resetSelectedOrders();
    refetch();
    queryClient.invalidateQueries(['order-count']);
  };

  const hasResolveOnHold = useMemo(
    () => activeData.some((item) => !!item.merchant_action_required),
    [activeData],
  );

  const allOrdersInCurrentPageAreSelected =
    allOrdersInCurrentPage.length === selectedOrderIds.length &&
    new Set(allOrdersInCurrentPage).size === new Set(selectedOrderIds).size &&
    allOrdersInCurrentPage.every((item) => selectedOrderIds.includes(item));

  //* UseEffects
  useEffect(() => {
    // if user changes the url manually
    if (isValidOrderStatusTab(activeTabQuery) && activeTab !== activeTabQuery) {
      setActiveTab(activeTabQuery);
      // Don't clear search when changing tabs via URL - let the search initialization effect handle it
      if (
        (activeSubTabQuery as string) === 'action-required' ||
        listStatusButtons[activeTabQuery].some((item) => item.value === activeSubTabQuery)
      )
        setActiveSubTab(activeSubTabQuery ?? 'all');
    }
  }, [activeTabQuery, activeSubTabQuery, activeTab]);

  // No URL-based search sync needed — global search uses /search page

  function isValidOrderStatusTab(value: string): boolean {
    return Object.values(OrderStatusTab).includes(value as OrderStatusTab);
  }

  // active button
  function checkActive(item: string): 'primary' | 'default' {
    return activeSubTab === item ? 'primary' : 'default';
  }

  const handleChangeTab = (activeTabKey: OrderStatusTab) => {
    resetSelectionAndFilters();
    setActiveSubTab('all');
    // Clear global search when switching tabs
    if (searchTermHeader?.search) {
      setSearchTermHeader({ search: '', searchBy: '' });
    }
    setActiveTabQueryString(activeTabKey);
    queryClient.invalidateQueries(['order-count']);
  };

  /** Problematic tab only (owner 11-02): Action required on, or back to all problematic orders. */
  function handleToggleActionRequired() {
    handleChangeSubTab(activeSubTab === 'action-required' ? 'all' : 'action-required');
  }

  function handleChangeSubTab(subTabKey: string) {
    // Only reset selection, NOT filters — status sub-tab change should preserve Type/Date filters
    resetSelectedOrders();
    setActiveSubTab(subTabKey);
    setActiveTabQueryString(activeTab, subTabKey);
    // BOXY-595 — the list opens fresh, so the chip counts must too.
    queryClient.invalidateQueries(['order-count']);
  }

  function resetSelectionAndFilters() {
    //reset selection
    resetSelectedOrders();

    //reset filters
    resetFilers();

    // Clear filter parameters from URL only if on orders page
    if (location.pathname.startsWith('/orders')) {
      const params = new URLSearchParams(location.search);

      params.delete('createdFrom');
      params.delete('createdTo');
      params.delete('orderType');

      setSearchParams(params);
    }
  }

  const resetSelectedOrders = () => {
    if (isAllSelected) setIsAllSelected(false);
    setSelectedOrderIds([]);
  };

  const handleUpdateSelectedOrderIds = (selectedKeys: string[]) => {
    if (isAllSelected) setIsAllSelected(false);
    setSelectedOrderIds(selectedKeys);
  };

  const setActiveTabQueryString = (activeTabKey: OrderStatusTab, activeSubTabKey?: string) => {
    // Only update query string if on orders page
    if (!location.pathname.startsWith('/orders')) {
      return;
    }

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('activeTab', activeTabKey);

      if (activeSubTabKey) {
        params.set('activeSubTab', activeSubTabKey);
      } else {
        params.delete('activeSubTab');
      }

      params.set('page', '1');
      // Clean up search params when changing tabs
      params.delete('search');
      params.delete('globalSearch');

      return params;
    });
  };

  async function handleBulkDelete() {
    const order_count = isAllSelected ? totals : selectedOrderIds.length;
    openConfirm({
      description: t('cancel_order_confirm', { order_count }),
      cancelText: t('cancel'),
      onConfirm: () => {
        const selectedIds = isAllSelected ? [] : selectedOrderIds;

        if (activeTab === OrderStatusTab.READY_TO_SHIP && !isScheduledOnly) {
          bulkDeleteOrder(
            {
              status: OrderStatusEnum.new,
              selectedIds,
            },
            {
              onSuccess: () => {
                resetSelectedOrders();
                refetch();
                queryClient.invalidateQueries(['order-count']);
              },
              onError: () => {
                message.error(t('operation_failed_please_try_again'));
              },
            },
          );
        } else {
          bulkCancelOrder(
            {
              status: activeSubTab as OrderStatusEnum,
              selectedIds,
            },
            {
              onSuccess: () => {
                resetSelectedOrders();
                refetch();
                queryClient.invalidateQueries(['order-count']);
              },
              onError: () => {
                message.error(t('operation_failed_please_try_again'));
              },
            },
          );
        }
      },
    });
  }

  const handleToggleIsAllSelected = () => {
    if (isAllSelected) setSelectedOrderIds([]);
    setIsAllSelected(!isAllSelected);
  };

  return (
    <PageLayout
      bottomSheet={
        isSearched ? (
          <BottomSheet
            sticky
            isVisible={selectedOrderIds.length > 0}
            numberSelected={selectedOrderIds.length}
            withoutButton
            total={activeTab === OrderStatusTab.INVENTORY ? inventoryTotals : totals}
            menuItems={
              activeTab === OrderStatusTab.INVENTORY ? inventoryMenuItems : menuItems.slice(0, 1)
            }
          />
        ) : (
          <BottomSheet
            sticky
            justShowAdditionalContent={isMixedReadyToShip}
            withoutButton={
              [OrderStatusTab.FINISHED, OrderStatusTab.RTO, OrderStatusTab.INVENTORY].includes(
                activeTab,
              ) ||
              isMixedReadyToShip ||
              exportOnlySelectAll
            }
            disableDropdown={isMixedReadyToShip}
            isVisible={selectedOrderIds.length > 0 || isAllSelected}
            numberSelected={
              isAllSelected
                ? activeTab === OrderStatusTab.INVENTORY
                  ? inventoryTotals
                  : totals
                : selectedOrderIds.length
            }
            total={activeTab === OrderStatusTab.INVENTORY ? inventoryTotals : totals}
            labelBtn={
              activeTab === OrderStatusTab.IN_PROGRESS ||
              activeTab === OrderStatusTab.ON_HOLD ||
              isScheduledOnly
                ? t('download_label')
                : t('ship_new_orders')
            }
            Icon={
              activeTab === OrderStatusTab.IN_PROGRESS ||
              activeTab === OrderStatusTab.ON_HOLD ||
              isScheduledOnly ? (
                <DownloadIcon />
              ) : (
                <PickupsIcon width={20} height={20} />
              )
            }
            onSubmit={onSubmit}
            loadingSubmit={LoadingBulkRequestPickup}
            menuItems={
              exportOnlySelectAll
                ? menuItems.slice(0, 1)
                : getBulkMenuItems(activeTab, menuItems, bulkSupportsSelectAll)
            }
            additionalContent={
              exportOnlySelectAll ? undefined : isMixedReadyToShip ? (
                <div className='flex items-center gap-3 w-full px-1'>
                  <span className='text-[13px] text-sd-foreground'>
                    {t('orders_are_combined', {
                      defaultValue:
                        'Your selection contains new and scheduled orders. Choose a type to continue:',
                    })}
                  </span>
                  <div className='flex items-center gap-1.5 ms-auto shrink-0'>
                    <button
                      type='button'
                      className='group flex items-center gap-2 h-10 px-5 rounded-xl bg-sd-primary text-sd-primary-foreground text-[12px] font-semibold cursor-pointer hover:brightness-95 active:scale-[0.97] transition-all whitespace-nowrap shadow-sm'
                      onClick={() => {
                        setPendingAutoSelect(true);
                        handleChangeSubTab('new');
                      }}
                    >
                      <PickupsIcon width={14} height={14} />
                      {t('new_orders')}
                      <span className='inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-sd-primary-foreground/15 text-[10px] font-bold'>
                        {readyToShipCounts?.new ?? 0}
                      </span>
                    </button>
                    <button
                      type='button'
                      className='group flex items-center gap-2 h-10 px-5 rounded-xl border border-solid border-sd-border bg-sd-card text-sd-foreground text-[12px] font-semibold cursor-pointer hover:bg-sd-muted active:scale-[0.97] transition-all whitespace-nowrap'
                      onClick={() => {
                        setPendingAutoSelect(true);
                        handleChangeSubTab('scheduled');
                      }}
                    >
                      <DownloadIcon />
                      {t('scheduled')}
                      <span className='inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-sd-muted text-[10px] font-bold text-sd-muted-foreground'>
                        {readyToShipCounts?.scheduled ?? 0}
                      </span>
                    </button>
                  </div>
                </div>
              ) : activeTab === OrderStatusTab.INVENTORY ? (
                <div className='flex gap-2'>
                  <button
                    type='button'
                    className='flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[12px] font-bold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors uppercase whitespace-nowrap'
                    onClick={() => {
                      const ordersToResend = inventoryRawItems.filter((item) =>
                        selectedOrderIds.includes(item.uid),
                      );
                      setResendOrders(ordersToResend);
                      navigate(ROUTE.RESEND);
                    }}
                  >
                    <RotateCw size={14} />
                    {t('resend_selected')}
                  </button>
                  <button
                    type='button'
                    className='flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[rgb(239,68,68)] text-white text-[12px] font-bold cursor-pointer hover:bg-[rgb(220,38,38)] transition-colors uppercase whitespace-nowrap'
                    onClick={() => {
                      const ordersToReturn = inventoryRawItems.filter((item) =>
                        selectedOrderIds.includes(item.uid),
                      );
                      setDelayRtoTargetOrders(ordersToReturn);
                      setDelayRtoModalOpen(true);
                    }}
                  >
                    <Undo2 size={14} />
                    {t('return_selected')}
                  </button>
                </div>
              ) : hasPermission(PermissionName.RESOLVE_OR_REQUEST_RTO) ? (
                <BulkOrderActions rows={selectedRows} onDone={handleBulkActionDone} />
              ) : undefined
            }
            isAllSelected={isAllSelected}
            toggleIsAllSelected={handleToggleIsAllSelected}
            shouldHaveSelectAll={allOrdersInCurrentPageAreSelected && !isMixedReadyToShip}
          />
        )
      }
    >
      <Flex vertical gap={24} className='w-full'>
        <OrdersPausedBanner />
        <div className='orders-list-card min-w-0'>
          {!isGlobalSearch && (
            <OrdersToolbar
              view={{ tabs: tabItems, activeTab, onTabChange: handleChangeTab }}
              columnOptions={tableColumns.options}
              filter={{
                activeSubTab,
                onSubTabChange: handleChangeSubTab,
                onToggleActionRequired: handleToggleActionRequired,
                actionRequiredCount: orderCount?.object?.[OrderStatusTab.ON_HOLD]?.action_required,
                statusCounts: orderCount?.object?.[activeTab] as Record<string, number> | undefined,
              }}
              summary={{
                total: activeTab === OrderStatusTab.INVENTORY ? inventoryTotals : totals,
              }}
              onBulkResolve={
                activeTab === OrderStatusTab.ON_HOLD &&
                hasPermission(PermissionName.RESOLVE_OR_REQUEST_RTO)
                  ? () => setIsBulkResolveOpen(true)
                  : undefined
              }
            />
          )}

          <TableAction<OrderDataType & OrderColumn>
            columns={tableColumns.visible}
            selectedRowKeys={selectedOrderIds}
            setSelectedRowKeys={handleUpdateSelectedOrderIds}
            data={
              activeTab === OrderStatusTab.INVENTORY
                ? inventoryData
                : isSearched
                  ? listData
                  : activeData
            }
            pageSize={pageSize}
            current={activeTab === OrderStatusTab.INVENTORY ? inventoryPage : currentPage}
            loading={
              activeTab === OrderStatusTab.INVENTORY
                ? isInventoryFetching
                : isSearched
                  ? fetchingOrderList
                  : isFetching
            }
            total={
              activeTab === OrderStatusTab.INVENTORY
                ? inventoryTotals
                : isSearched
                  ? totalsOrderList
                  : totals
            }
            onPaginationChange={
              activeTab === OrderStatusTab.INVENTORY ? setInventoryPage : setCurrentPage
            }
            rowSelectionEnabled
            header={undefined}
            onRow={(record) => ({
              onClick: (e) => {
                // Don't navigate if clicking on interactive elements in the row
                const target = e.target as Element;
                // Walk up from target to find if we're in an interactive context
                let el: Element | null = target;
                while (el && el.tagName !== 'TR') {
                  const tag = el.tagName?.toUpperCase();
                  if (tag === 'BUTTON' || tag === 'A' || tag === 'SVG' || tag === 'PATH') return;
                  if (el instanceof HTMLElement) {
                    if (el.classList?.contains('ant-checkbox-wrapper')) return;
                    if (el.classList?.contains('ant-dropdown-trigger')) return;
                    if (el.getAttribute?.('data-testid') === 'action-dropdown') return;
                  }
                  el = el.parentElement;
                }
                navigate(`/orders/${record.key}`);
              },
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      </Flex>
      <BulkResolveModal isOpen={isBulkResolveOpen} onClose={() => setIsBulkResolveOpen(false)} />
      <DelayRtoConfirmModal
        open={delayRtoModalOpen}
        orders={delayRtoTargetOrders}
        onClose={() => {
          setDelayRtoModalOpen(false);
          setDelayRtoTargetOrders([]);
        }}
        onSuccess={() => {
          setDelayRtoTargetOrders([]);
          setSelectedOrderIds([]);
        }}
      />
    </PageLayout>
  );
};

export default Orders;
